// Considered: ProgressBarAtom — rejected because it is a single-value track with no segments; this bar shows one
// segment per member in order, a neutral hidden run and a hatched not-yet-measured run. See spec Reuse Audit.
import React from 'react';
import type { StatusValue } from 'types/spherePoints';
import './ShareBarAtom.scss';

/** `id` identifies the segment across renders: a member's sphere id, or one id per merged run, the hidden run and the unmeasured run. */
export type ShareBarSegment =
  | { id: string; kind: 'status'; status: StatusValue; share: number }
  | { id: string; kind: 'hidden'; share: number }
  | { id: string; kind: 'unmeasured'; share: number };

export interface ShareBarAtomProps {
  /** Segments in draw order; each grows by its `share` of the track. */
  segments: ShareBarSegment[];
  /** Words for the whole bar, read by assistive tech in place of the drawing; segments carry no text of their own. */
  label: string;
  className?: string;
}

const segmentClass = (segment: ShareBarSegment): string =>
  segment.kind === 'status'
    ? `share-bar-atom__segment share-bar-atom__segment--status share-bar-atom__segment--${segment.status.replaceAll('_', '-')}`
    : `share-bar-atom__segment share-bar-atom__segment--${segment.kind}`;

/**
 * Pill track of rounded segments in member order, with the hatched not-yet-measured run last. The drawing is
 * hidden from assistive tech, which reads `label` from a visually hidden sibling instead.
 */
const ShareBarAtom: React.FC<ShareBarAtomProps> = ({ segments, label, className = '' }) => (
  <>
    <span className="share-bar-atom__label">{label}</span>
    <div className={`share-bar-atom ${className}`.trim()} aria-hidden="true" data-testid="share-bar">
      {segments
        .filter((segment) => segment.share > 0)
        .map((segment) => (
          <span
            key={segment.id}
            className={segmentClass(segment)}
            data-kind={segment.kind}
            data-status={segment.kind === 'status' ? segment.status : undefined}
            style={{ flexGrow: segment.share }}
          />
        ))}
    </div>
  </>
);

export default ShareBarAtom;
